import React from "react";
import PropTypes from "prop-types";
import "../../assets/scss/custom/components/SecondaryButton.scss";

/**
 * SecondaryButton
 * Secondary button style with solid background and icon support.
 * Used for secondary actions like "Connect Account", "View All", etc.
 *
 * variant="orange" -> orange background (default)
 * variant="green" -> green background
 * variant="purple" -> purple background
 * variant="brown" -> brown background
 * variant="white" -> white background
 * variant="black" -> black background
 *
 *
 * Usage:
 * <SecondaryButton variant="orange" icon={linkIcon} onClick={...}>Connect Account</SecondaryButton>
 * OR
 * <SecondaryButton label="Connect Account" variant="orange" icon={linkIcon} onClick={...} />
 */
const SecondaryButton = ({
  label,
  children,
  variant = "orange",
  type = "button",
  icon,
  iconPosition = "right",
  onClick,
  disabled,
  className,
  loading,
  ...rest
}) => {
  const renderIcon = () => {
    if (!icon) return null;
    return <img src={icon} alt="" className="secondary-btn-icon" />;
  };

  const noIconClass = !icon ? " secondary-btn-no-icon" : "";
  const buttonText = label || children;
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={`secondary-btn secondary-btn-${variant}${noIconClass} ${className || ""} ${loading ? "secondary-btn-loading" : ""}`}
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      {loading ? (
        <span className="secondary-btn-loader">
          <span className="secondary-btn-spinner"></span>
        </span>
      ) : (
        <>
          {iconPosition === "left" && renderIcon()}
          <span className="secondary-btn-label">{buttonText}</span>
          {iconPosition === "right" && renderIcon()}
        </>
      )}
    </button>
  );
};

SecondaryButton.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.oneOf(["orange", "green", "purple", "brown", "white", "white-transparent", "lightOrange", "light-orange", "light-grey", "red", "light-red", "black"]),
  type: PropTypes.string,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
};

export default SecondaryButton;
